import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios/index';
import ReCAPTCHA from 'react-google-recaptcha';
import yup from 'yup';
import FormTextField from './FormTextField';

/**
 * The form schema definition.
 */
const contactFormSchema = yup.object({
  name: yup
    .string()
    .ensure()
    .trim()
    .required('Please let me know who you are'),
  email: yup
    .string()
    .ensure()
    .trim()
    .required('Please provide an email address I can use to get back to you')
    .email('The email address does not appear to be valid'),
  subject: yup
    .string()
    .ensure()
    .trim()
    .required('Please enter a subject'),
  message: yup
    .string()
    .ensure()
    .trim()
    .required('Please enter a message'),
});

export default class ContactForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // 'filling' | 'submitting' | 'error' | 'submitted'
      status: 'filling',

      // form values
      inputValues: contactFormSchema.default(),

      // form errors
      inputErrors: {},

      // form fields touched/blurred
      inputBlurs: {},
    };
  }

  componentDidMount() {
    // run an initial validate to pre-populate input errors
    // (prevents immediately initiating a submit)
    this.validateInputs();
  }

  /**
   * Sets failure state of form submission.
   * @param {Object} errors The errors returned by the server.
   */
  setSubmitFail = errors => {
    this.captcha.reset();
    console.log(errors);

    this.setState({ status: 'error' });
  };

  /**
   * Sets success state of form submission.
   */
  setSubmitSuccess = () => {
    this.setState({
      status: 'submitted',
      inputValues: contactFormSchema.default(),
      inputBlurs: {},
    });

    this.validateInputs();
  };

  captcha;

  /**
   *  Invoke CAPTCHA check.
   */
  checkCaptcha = event => {
    event.preventDefault();

    if (this.isValidateForSubmit()) {
      this.captcha.execute();
    }
  };

  /**
   * Handle CAPTCHA change.
   * @param value
   */
  handleCaptchaChange = value => {
    // if we got a new CAPTCHA value go ahead and submit the form
    if (value) {
      this.handleSubmit(value);
    }
  };

  handleFieldBlur = event => {
    const blur = this.state.inputBlurs;
    blur[event.target.name] = true;

    const newState = { status: 'filling', inputBlurs: blur };

    this.setState(newState, this.validateInputs);
  };

  handleFieldChange = event => {
    const { inputValues } = this.state;
    inputValues[event.target.name] = event.target.value;

    const blur = this.state.inputBlurs;
    blur[event.target.name] = true;

    const newState = { status: 'filling', inputValues, inputBlurs: blur };

    this.setState(newState, this.validateInputs);
  };

  /**
   * Handles form submission.
   */
  handleSubmit = captchaValue => {
    this.setState({ status: 'submitting' });

    const { inputValues } = this.state;
    inputValues['g-recaptcha-response'] = captchaValue;

    const action = `${process.env.API_URL}/contacts`;
    axios
      .post(action, inputValues)
      .then(response => this.handleSubmitResponse(response))
      .catch(error => {
        let errors = {};
        if (error.response) {
          if (error.response.data.message) {
            errors.push(error.response.data.message);
          } else {
            errors = error.response.data.errors || {};
          }
        }

        this.setSubmitFail(errors);
      });
  };

  /**
   * Handles non-error responses of form submission.
   * @param {Object} response The response object of the submit.
   */
  handleSubmitResponse = response => {
    this.captcha.reset();
    if (response.status === 200) {
      this.setSubmitSuccess();
    } else {
      const errors = response.data.errors || {};
      this.setSubmitFail(errors);
    }
  };

  /**
   * Determines if form is valid for submission.
   * @return {boolean} true if form is valid, otherwise false.
   */
  isValidateForSubmit = () => {
    if (Object.keys(this.state.inputErrors).length > 0) {
      // force blur on all inputs to ensure validation messages are displayed
      const inputBlurs = contactFormSchema.default();
      Object.keys(inputBlurs).forEach(key => {
        inputBlurs[key] = true;
      });
      this.setState({ status: 'error', inputBlurs });

      return false;
    }

    return true;
  };

  validateInputs = () => {
    contactFormSchema
      .validate(this.state.inputValues, { abortEarly: false })
      .then(this.setState({ inputErrors: {} }))
      .catch(validationError => {
        const inputErrors = {};
        if (Array.isArray(validationError.inner)) {
          validationError.inner.forEach(error => {
            inputErrors[error.path] = error.message;
          });
        } else {
          inputErrors[validationError.path] = validationError.message;
        }

        this.setState({ inputErrors });
      });
  };

  render() {
    const { status } = this.state;
    const { successMessage: SuccessMessage, failMessage: FailMessage } = this.props;

    return (
      <form noValidate onSubmit={this.checkCaptcha}>
        <FormTextField
          type="text"
          id="contact-name"
          name="name"
          label="Your Name (required)"
          value={this.state.inputValues.name}
          placeholder="Enter your name"
          required
          onBlur={this.handleFieldBlur}
          onChange={this.handleFieldChange}
          errorMessage={this.state.inputBlurs.name ? this.state.inputErrors.name : ''}
        />
        <FormTextField
          type="email"
          id="contact-email"
          name="email"
          label="Your Email (required)"
          value={this.state.inputValues.email}
          placeholder="Enter your email address"
          required
          onBlur={this.handleFieldBlur}
          onChange={this.handleFieldChange}
          errorMessage={this.state.inputBlurs.email ? this.state.inputErrors.email : ''}
        />
        <FormTextField
          type="text"
          id="contact-subject"
          name="subject"
          label="Subject (required)"
          value={this.state.inputValues.subject}
          placeholder="Enter a subject"
          required
          onBlur={this.handleFieldBlur}
          onChange={this.handleFieldChange}
          errorMessage={this.state.inputBlurs.subject ? this.state.inputErrors.subject : ''}
        />
        <FormTextField
          type="textarea"
          id="contact-message"
          name="message"
          label="Message (required)"
          value={this.state.inputValues.message}
          placeholder="Enter a message"
          required
          onBlur={this.handleFieldBlur}
          onChange={this.handleFieldChange}
          errorMessage={this.state.inputBlurs.message ? this.state.inputErrors.message : ''}
        />
        <div className="field">
          <div className="control">
            <input type="submit" className="button is-primary is-uppercase" value="Send Message" />
          </div>
        </div>
        {status === 'error' && <FailMessage />}
        {status === 'submitted' && <SuccessMessage />}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
          <ReCAPTCHA
            ref={el => {
              this.captcha = el;
            }}
            sitekey={process.env.CAPTCHA_SITE_KEY}
            size="invisible"
            badge="inline"
            onChange={this.handleCaptchaChange}
          />
        </div>
      </form>
    );
  }
}

ContactForm.propTypes = {
  successMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  failMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
};



// WEBPACK FOOTER //
// ./src/components/ContactForm.js